import React from "react";
import { Loader2, CheckCircle2, AlertTriangle, RefreshCw, RotateCcw, Sparkles } from "lucide-react";
import { formatDuration } from "@/lib/routeOptimizer";

export function OptimizationBadge({ opt, optimizationState, stopCount, testIdPrefix: p }) {
  const base = "inline-flex items-center gap-1 rounded-full text-[10px] font-medium px-2 py-0.5";
  if (opt.optimizing) {
    return (
      <span className={`${base} bg-slate-100 text-slate-700`} data-testid={`${p}-opt-badge-optimizing`}>
        <Loader2 className="h-3 w-3 animate-spin" /> Optimizing…
      </span>
    );
  }
  if (opt.failure) {
    return (
      <span className={`${base} bg-red-600 text-white`} data-testid={`${p}-opt-badge-failed`}>
        <AlertTriangle className="h-3 w-3" /> NOT optimized
      </span>
    );
  }
  if (optimizationState === "optimized") {
    const r = opt.lastResult;
    return (
      <span className={`${base} bg-emerald-100 text-emerald-800`} data-testid={`${p}-opt-badge-optimized`}>
        <CheckCircle2 className="h-3 w-3" /> Optimized
        {r ? ` · ${formatDuration(r.totalSeconds)} · ${(r.totalMeters / 1000).toFixed(1)} km` : ""}
      </span>
    );
  }
  if (optimizationState === "manual") {
    return (
      <span className={`${base} bg-amber-100 text-amber-800`} data-testid={`${p}-opt-badge-manual`}>
        Manual order
      </span>
    );
  }
  if (stopCount >= 2) {
    return (
      <span className={`${base} bg-slate-100 text-slate-600`} data-testid={`${p}-opt-badge-pending`}>
        Not optimized yet
      </span>
    );
  }
  return null;
}

export function ReoptimizeButton({ opt, testIdPrefix: p }) {
  if (!opt.canOptimize) return null;
  return (
    <button
      type="button"
      onClick={() => opt.runOptimization("manual")}
      disabled={opt.optimizing || !opt.startReady}
      className="inline-flex items-center gap-1 text-[11px] text-slate-600 hover:text-slate-900 disabled:opacity-50 px-2 py-1 rounded border border-slate-300 bg-white"
      data-testid={`${p}-reoptimize-button`}
    >
      {opt.optimizing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
      Re-optimize
    </button>
  );
}

export function OptimizationFailureBanner({ opt, testIdPrefix: p }) {
  const f = opt.failure;
  if (!f || opt.optimizing) return null;
  return (
    <div
      role="alert"
      className="mb-3 rounded-md border-2 border-red-500 bg-red-50 p-3 text-red-900"
      data-testid={`${p}-opt-error`}
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold uppercase tracking-wide" data-testid={`${p}-opt-error-title`}>
            Route optimization failed — stops are NOT optimized
          </div>
          <div className="text-sm mt-1" data-testid={`${p}-opt-error-explanation`}>
            {f.explanation}
          </div>
          <div className="text-[11px] mt-1 font-mono text-red-700 break-words" data-testid={`${p}-opt-error-code`}>
            {f.code}
            {f.message && f.message !== f.code ? ` · ${f.message}` : ""}
          </div>
          <div className="text-xs mt-2 text-red-800">
            Stops below are in your selection order. Fix the cause and retry, or reorder manually with the arrows.
            This failure has been logged to Admin → Errors.
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => opt.runOptimization("retry")}
              disabled={!opt.startReady}
              className="inline-flex items-center gap-1.5 rounded bg-red-600 hover:bg-red-700 text-white text-xs font-semibold px-3 py-1.5 min-h-[36px] disabled:opacity-50"
              data-testid={`${p}-opt-retry-button`}
            >
              <RefreshCw className="h-3.5 w-3.5" /> Retry optimization
            </button>
            {f.code === "DIRECTIONS_MODULE_FAILED" || f.code === "TIMEOUT" || f.code === "MAPS_NOT_LOADED" ? (
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="inline-flex items-center gap-1.5 rounded border border-red-300 bg-white hover:bg-red-100 text-red-800 text-xs font-semibold px-3 py-1.5 min-h-[36px]"
                data-testid={`${p}-opt-reload-button`}
              >
                <RotateCcw className="h-3.5 w-3.5" /> Reload page
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
